import { createContext, useContext, useState, ReactNode, useCallback, useEffect, useRef } from 'react';
import { dashboardApi, type DashboardSectionResponse } from './api';
import {
  anySectionLoading,
  assembleDashboardResponse,
  DASHBOARD_CORE_SECTIONS,
  emptySectionLoading,
  type DashboardSection,
} from './dashboardSections';
import { formatDashboardDate } from './dashboardHelpers';
import { useAppSocket } from '../../providers/AppSocketContext';
import { useAuth } from '../../hooks/useAuth';
import { AdvancedDashboardData, emptyAdvancedData } from './dashboardAdvancedTypes';
import {
  buildDashboardCacheKey,
  buildDashboardFingerprint,
  getDashboardCache,
  isLegacyDashboardCache,
  parseDashboardResponse,
  saveDashboardCache,
} from './dashboardCache';
import type { DashboardCachedPayload } from '../../db/localCache';
import { useSessionState } from '../../hooks/useSessionState';

export type DashboardViewMode = 'simple' | 'advanced' | 'users' | 'timeline';

const VIEW_MODE_STORAGE_KEY = 'dashboard.viewMode';

export interface DashboardDateRange {
    from: Date;
    to: Date;
}

export interface KPIData {
    totalSales: number;
    totalProfit: number;
    projectedProfit: number;
    salesCount: number;
    avgOrderValue: number;
    totalDebt: number;
    clientCount: number;
    pendingDeposits: number;
    totalInventoryValue: number;
    totalPayments: number;
    paymentCount: number;
    totalSupplyValue: number;
    totalSupplierPayments: number;
    supplierBalanceOwed: number;
    overdueSupplyCount: number;
    totalAgentDebt: number;
    creditRiskCount: number;
    walletBalance: number;
    walletCount: number;
    walletActiveCount: number;
    walletCredits: number;
    walletDebits: number;
    walletMovementCount: number;
}

export interface SalesDataPoint {
    date: string;
    amount: number;
    count: number;
}

export interface PaymentModeData {
    mode: string;
    amount: number;
}

export interface DebtorData {
    name: string;
    balance: number;
    credit_limit: number;
}

export interface InventoryDepotData {
    depot: string;
    depotId: number;
    totalItems: number;
    totalValue: number;
    lowStockCount: number;
}

export type RecentTransactionType =
    | 'sale'
    | 'supply'
    | 'supply_return'
    | 'supply_payment'
    | 'movement'
    | 'discard'
    | 'restore'
    | 'inventory'
    | 'conversion'
    | 'payment'
    | 'production'
    | 'bom'
    | 'wallet'
    | 'expense'
    | 'voucher'
    | 'deposit';

export interface RecentTransaction {
    id: string;
    type: RecentTransactionType;
    reference: string;
    date: string;
    amount: number;
    description: string;
    depot?: string;
    user?: string;
    createdAt?: string;
    updatedAt?: string;
    batch?: string;
    count?: number;
    clientId?: number;
    clientName?: string;
}

export interface TopWalletData {
    clientId: number;
    name: string;
    balance: number;
    isActive: boolean;
}

export interface WalletTagData {
    tag: string;
    credit: number;
    debit: number;
    count: number;
    amount: number;
}

export interface MovementTypeCount {
    type: string;
    count: number;
}

export interface ConversionTypeCount {
    type: string;
    count: number;
}

export interface SalesByFamilyData {
    family: string;
    amount: number;
}

export interface SalesByCategoryData {
    category: string;
    amount: number;
}

export interface SalesByDepotData {
    depot: string;
    amount: number;
}

export interface SalesBySectorData {
    sector: string;
    amount: number;
}

export interface SalesByAgentData {
    agent: string;
    amount: number;
}

export interface PaymentsByUserData {
    user: string;
    amount: number;
}

export interface PaymentsByDepotData {
    depot: string;
    amount: number;
}

export interface TopItemData {
    name: string;
    quantity: number;
    total: number;
}

export interface PaymentTypeData {
    type: string;
    amount: number;
}

export interface SupplierData {
    supplier: string;
    totalSupplied: number;
    totalPaid?: number;
    balance?: number;
    itemCount: number;
}

export interface SupplierBalanceData {
    supplier: string;
    balance: number;
}

export interface OverdueSupplyData {
    batch: string;
    supplier: string;
    balance: number;
    dueDate: string;
}

export interface SalesProfitRow {
    category: string;
    depot: string;
    salesAmount: number;
    costAmount: number;
    profit: number;
}

export interface ProjectedProfitRow {
    category: string;
    depot: string;
    potentialRevenue: number;
    costAmount: number;
    projectedProfit: number;
}

export interface CreditRiskClient {
    name: string;
    balance: number;
    creditLimit: number;
    utilizationPct: number;
}

interface DashboardContextType {
    dateRange: DashboardDateRange;
    setDateRange: (range: DashboardDateRange) => void;
    viewMode: DashboardViewMode;
    setViewMode: (mode: DashboardViewMode) => void;
    refreshGeneration: number;
    kpi: KPIData;
    advanced: AdvancedDashboardData;
    salesTrend: SalesDataPoint[];
    salesProfitBreakdown: SalesProfitRow[];
    projectedProfitBreakdown: ProjectedProfitRow[];
    paymentModes: PaymentModeData[];
    topDebtors: DebtorData[];
    topAgents: DebtorData[];
    inventoryByDepot: InventoryDepotData[];
    recentTransactions: RecentTransaction[];
    movementCounts: MovementTypeCount[];
    conversionCounts: ConversionTypeCount[];
    salesByFamily: SalesByFamilyData[];
    salesByCategory: SalesByCategoryData[];
    salesByDepot: SalesByDepotData[];
    salesBySector: SalesBySectorData[];
    salesByAgent: SalesByAgentData[];
    topItems: TopItemData[];
    paymentTypes: PaymentTypeData[];
    paymentsByUser: PaymentsByUserData[];
    paymentsByDepot: PaymentsByDepotData[];
    supplierData: SupplierData[];
    supplierPaymentModes: PaymentModeData[];
    topSupplierBalances: SupplierBalanceData[];
    overdueSupplies: OverdueSupplyData[];
    creditRiskClients: CreditRiskClient[];
    topWallets: TopWalletData[];
    walletByTag: WalletTagData[];
    loading: boolean;
    sectionLoading: Record<DashboardSection, boolean>;
    revalidating: boolean;
    staleDataAvailable: boolean;
    cachedAt: Date | null;
    refresh: () => void;
    applyPendingRefresh: () => void;
    dismissStaleAlert: () => void;
    currency: string;
}

const defaultKPI: KPIData = {
    totalSales: 0,
    totalProfit: 0,
    projectedProfit: 0,
    salesCount: 0,
    avgOrderValue: 0,
    totalDebt: 0,
    clientCount: 0,
    pendingDeposits: 0,
    totalInventoryValue: 0,
    totalPayments: 0,
    paymentCount: 0,
    totalSupplyValue: 0,
    totalSupplierPayments: 0,
    supplierBalanceOwed: 0,
    overdueSupplyCount: 0,
    totalAgentDebt: 0,
    creditRiskCount: 0,
    walletBalance: 0,
    walletCount: 0,
    walletActiveCount: 0,
    walletCredits: 0,
    walletDebits: 0,
    walletMovementCount: 0,
};

const emptyDashboardState = {
    kpi: defaultKPI,
    salesTrend: [] as SalesDataPoint[],
    salesProfitBreakdown: [] as SalesProfitRow[],
    projectedProfitBreakdown: [] as ProjectedProfitRow[],
    paymentModes: [] as PaymentModeData[],
    topDebtors: [] as DebtorData[],
    topAgents: [] as DebtorData[],
    inventoryByDepot: [] as InventoryDepotData[],
    recentTransactions: [] as RecentTransaction[],
    movementCounts: [] as MovementTypeCount[],
    conversionCounts: [] as ConversionTypeCount[],
    salesByFamily: [] as SalesByFamilyData[],
    salesByCategory: [] as SalesByCategoryData[],
    salesByDepot: [] as SalesByDepotData[],
    salesBySector: [] as SalesBySectorData[],
    salesByAgent: [] as SalesByAgentData[],
    topItems: [] as TopItemData[],
    paymentTypes: [] as PaymentTypeData[],
    paymentsByUser: [] as PaymentsByUserData[],
    paymentsByDepot: [] as PaymentsByDepotData[],
    supplierData: [] as SupplierData[],
    supplierPaymentModes: [] as PaymentModeData[],
    topSupplierBalances: [] as SupplierBalanceData[],
    overdueSupplies: [] as OverdueSupplyData[],
    creditRiskClients: [] as CreditRiskClient[],
    topWallets: [] as TopWalletData[],
    walletByTag: [] as WalletTagData[],
};

const readStoredViewMode = (): DashboardViewMode => {
    try {
        const stored = localStorage.getItem(VIEW_MODE_STORAGE_KEY);
        if (stored === 'advanced' || stored === 'users' || stored === 'simple' || stored === 'timeline') {
            return stored;
        }
        return 'simple';
    } catch {
        return 'simple';
    }
};

const DashboardContext = createContext<DashboardContextType | undefined>(undefined);

export const DashboardProvider = ({ children }: { children: ReactNode }) => {
    const { appSettings } = useAppSocket();
    const { session } = useAuth();
    const today = new Date();
    const thirtyDaysAgo = new Date();
    thirtyDaysAgo.setDate(today.getDate() - 30);

    // Kept for the session so coming back to the dashboard keeps the chosen period.
    const [dateRange, setDateRange] = useSessionState<DashboardDateRange>(
        'dashboard:dateRange',
        { from: thirtyDaysAgo, to: today },
        (stored) => {
            const from = new Date(stored?.from);
            const to = new Date(stored?.to);
            return Number.isNaN(from.getTime()) || Number.isNaN(to.getTime()) ? undefined : { from, to };
        },
    );
    const [viewMode, setViewModeState] = useState<DashboardViewMode>(readStoredViewMode);
    const [kpi, setKpi] = useState<KPIData>(defaultKPI);
    const [advanced, setAdvanced] = useState<AdvancedDashboardData>(emptyAdvancedData);
    const [salesTrend, setSalesTrend] = useState<SalesDataPoint[]>([]);
    const [salesProfitBreakdown, setSalesProfitBreakdown] = useState<SalesProfitRow[]>([]);
    const [projectedProfitBreakdown, setProjectedProfitBreakdown] = useState<ProjectedProfitRow[]>([]);
    const [paymentModes, setPaymentModes] = useState<PaymentModeData[]>([]);
    const [topDebtors, setTopDebtors] = useState<DebtorData[]>([]);
    const [topAgents, setTopAgents] = useState<DebtorData[]>([]);
    const [inventoryByDepot, setInventoryByDepot] = useState<InventoryDepotData[]>([]);
    const [recentTransactions, setRecentTransactions] = useState<RecentTransaction[]>([]);
    const [movementCounts, setMovementCounts] = useState<MovementTypeCount[]>([]);
    const [conversionCounts, setConversionCounts] = useState<ConversionTypeCount[]>([]);
    const [salesByFamily, setSalesByFamily] = useState<SalesByFamilyData[]>([]);
    const [salesByCategory, setSalesByCategory] = useState<SalesByCategoryData[]>([]);
    const [salesByDepot, setSalesByDepot] = useState<SalesByDepotData[]>([]);
    const [salesBySector, setSalesBySector] = useState<SalesBySectorData[]>([]);
    const [salesByAgent, setSalesByAgent] = useState<SalesByAgentData[]>([]);
    const [topItems, setTopItems] = useState<TopItemData[]>([]);
    const [paymentTypes, setPaymentTypes] = useState<PaymentTypeData[]>([]);
    const [paymentsByUser, setPaymentsByUser] = useState<PaymentsByUserData[]>([]);
    const [paymentsByDepot, setPaymentsByDepot] = useState<PaymentsByDepotData[]>([]);
    const [supplierData, setSupplierData] = useState<SupplierData[]>([]);
    const [supplierPaymentModes, setSupplierPaymentModes] = useState<PaymentModeData[]>([]);
    const [topSupplierBalances, setTopSupplierBalances] = useState<SupplierBalanceData[]>([]);
    const [overdueSupplies, setOverdueSupplies] = useState<OverdueSupplyData[]>([]);
    const [creditRiskClients, setCreditRiskClients] = useState<CreditRiskClient[]>([]);
    const [topWallets, setTopWallets] = useState<TopWalletData[]>([]);
    const [walletByTag, setWalletByTag] = useState<WalletTagData[]>([]);
    const [loading, setLoading] = useState(true);
    const [sectionLoading, setSectionLoading] = useState<Record<DashboardSection, boolean>>(
        () => emptySectionLoading(true, false),
    );
    const [revalidating, setRevalidating] = useState(false);
    const [staleDataAvailable, setStaleDataAvailable] = useState(false);
    const [cachedAt, setCachedAt] = useState<Date | null>(null);
    const [refreshGeneration, setRefreshGeneration] = useState(0);

    const mountedRef = useRef(true);
    const pendingPayloadRef = useRef<DashboardCachedPayload | null>(null);
    const sectionResultsRef = useRef<Partial<Record<DashboardSection, DashboardSectionResponse>>>({});
    const requestGenRef = useRef(0);
    const dateRangeRef = useRef(dateRange);
    dateRangeRef.current = dateRange;
    const viewModeRef = useRef(viewMode);
    viewModeRef.current = viewMode;
    const hasMountedRef = useRef(false);

    useEffect(() => {
        return () => { mountedRef.current = false; };
    }, []);

    const setViewMode = useCallback((mode: DashboardViewMode) => {
        setViewModeState(mode);
        setStaleDataAvailable(false);
        pendingPayloadRef.current = null;
        try {
            localStorage.setItem(VIEW_MODE_STORAGE_KEY, mode);
        } catch {
            // ignore storage errors
        }
    }, []);

    const applyPayload = useCallback((payload: DashboardCachedPayload, wantAdvanced: boolean) => {
        const data = payload.base;
        setSectionLoading(emptySectionLoading(false, wantAdvanced));
        setKpi({ ...defaultKPI, ...(data.kpi || {}) });
        setSalesTrend(data.salesTrend || []);
        setSalesProfitBreakdown(data.salesProfitBreakdown || []);
        setProjectedProfitBreakdown(data.projectedProfitBreakdown || []);
        setPaymentModes(data.paymentModes || []);
        setTopDebtors(data.topDebtors || []);
        setTopAgents(data.topAgents || []);
        setInventoryByDepot(data.inventoryByDepot || []);
        setRecentTransactions(data.recentTransactions || []);
        setMovementCounts(data.movementCounts || []);
        setConversionCounts(data.conversionCounts || []);
        setSalesByFamily(data.salesByFamily || []);
        setSalesByCategory(data.salesByCategory || []);
        setSalesByDepot(data.salesByDepot || []);
        setSalesBySector(data.salesBySector || []);
        setSalesByAgent(data.salesByAgent || []);
        setTopItems(data.topItems || []);
        setPaymentTypes(data.paymentTypes || []);
        setPaymentsByUser(data.paymentsByUser || []);
        setPaymentsByDepot(data.paymentsByDepot || []);
        setSupplierData(data.supplierData || []);
        setSupplierPaymentModes(data.supplierPaymentModes || []);
        setTopSupplierBalances(data.topSupplierBalances || []);
        setOverdueSupplies(data.overdueSupplies || []);
        setCreditRiskClients(data.creditRiskClients || []);
        setTopWallets(data.topWallets || []);
        setWalletByTag(data.walletByTag || []);
        setAdvanced(
            wantAdvanced && payload.advanced
                ? payload.advanced
                : emptyAdvancedData,
        );
    }, []);

    const persistPayload = useCallback(async (
        cacheKey: string,
        from: string,
        to: string,
        mode: DashboardViewMode,
        payload: DashboardCachedPayload,
    ) => {
        // Users-activity is loaded separately and not part of the sales cache payload.
        const cacheMode = mode === 'advanced' ? 'advanced' : 'simple';
        const fingerprint = await buildDashboardFingerprint(payload, cacheMode);
        const fetchedAt = new Date().toISOString();
        const saved = await saveDashboardCache({
            cacheKey,
            from,
            to,
            mode: cacheMode,
            fingerprint,
            payload,
            fetchedAt,
        });
        if (saved && mountedRef.current) {
            setCachedAt(new Date(fetchedAt));
        }
        return { fingerprint, saved, fetchedAt };
    }, []);

    const applySection = useCallback((section: DashboardSection, data: DashboardSectionResponse) => {
        if (data.kpi) {
            setKpi((prev) => ({ ...prev, ...data.kpi }));
        }
        if (section === 'sales') {
            if (data.salesTrend !== undefined) setSalesTrend(data.salesTrend);
            if (data.salesProfitBreakdown !== undefined) {
                setSalesProfitBreakdown(data.salesProfitBreakdown);
            }
            if (data.salesByFamily !== undefined) setSalesByFamily(data.salesByFamily);
            if (data.salesByCategory !== undefined) setSalesByCategory(data.salesByCategory);
            if (data.salesByDepot !== undefined) setSalesByDepot(data.salesByDepot);
            if (data.salesBySector !== undefined) setSalesBySector(data.salesBySector);
            if (data.salesByAgent !== undefined) setSalesByAgent(data.salesByAgent);
            if (data.topItems !== undefined) setTopItems(data.topItems);
        } else if (section === 'payments') {
            if (data.paymentModes !== undefined) setPaymentModes(data.paymentModes);
            if (data.paymentTypes !== undefined) setPaymentTypes(data.paymentTypes);
            if (data.paymentsByUser !== undefined) setPaymentsByUser(data.paymentsByUser);
            if (data.paymentsByDepot !== undefined) setPaymentsByDepot(data.paymentsByDepot);
        } else if (section === 'inventory') {
            if (data.inventoryByDepot !== undefined) setInventoryByDepot(data.inventoryByDepot);
            if (data.projectedProfitBreakdown !== undefined) {
                setProjectedProfitBreakdown(data.projectedProfitBreakdown);
            }
        } else if (section === 'debt') {
            if (data.topDebtors !== undefined) setTopDebtors(data.topDebtors);
            if (data.topAgents !== undefined) setTopAgents(data.topAgents);
            if (data.creditRiskClients !== undefined) {
                setCreditRiskClients(data.creditRiskClients);
            }
        } else if (section === 'activity') {
            if (data.recentTransactions !== undefined) {
                setRecentTransactions(data.recentTransactions);
            }
            if (data.movementCounts !== undefined) setMovementCounts(data.movementCounts);
            if (data.conversionCounts !== undefined) setConversionCounts(data.conversionCounts);
            if (data.supplierData !== undefined) setSupplierData(data.supplierData);
            if (data.supplierPaymentModes !== undefined) {
                setSupplierPaymentModes(data.supplierPaymentModes);
            }
            if (data.topSupplierBalances !== undefined) {
                setTopSupplierBalances(data.topSupplierBalances);
            }
            if (data.overdueSupplies !== undefined) setOverdueSupplies(data.overdueSupplies);
        } else if (section === 'wallet') {
            if (data.topWallets !== undefined) setTopWallets(data.topWallets);
            if (data.walletByTag !== undefined) setWalletByTag(data.walletByTag);
        }
        setSectionLoading((prev) => ({ ...prev, [section]: false }));
    }, []);

    const fetchSectionsFromNetwork = useCallback(async (
        from: string,
        to: string,
        wantAdvanced: boolean,
        generation: number,
        onSection: (section: DashboardSection, data: DashboardSectionResponse) => void,
    ): Promise<DashboardCachedPayload | null> => {
        sectionResultsRef.current = {};
        let advancedData: AdvancedDashboardData | undefined;

        const sections =
            viewModeRef.current === 'simple' || viewModeRef.current === 'advanced'
                ? DASHBOARD_CORE_SECTIONS
                : [];
        if (sections.length === 0 && !wantAdvanced) {
            return null;
        }
        const sectionPromises = sections.map(async (section) => {
            const res = await dashboardApi.getSection(section, from, to);
            if (generation !== requestGenRef.current) return;
            sectionResultsRef.current[section] = res.data;
            onSection(section, res.data);
        });

        const advancedPromise = wantAdvanced
            ? dashboardApi.getAdvancedSection(from, to).then((res) => {
                  if (generation !== requestGenRef.current) return;
                  advancedData = {
                      ...emptyAdvancedData,
                      ...res.data.advanced,
                      kpi: { ...emptyAdvancedData.kpi, ...res.data.advanced.kpi },
                  };
                  setAdvanced(advancedData);
                  setSectionLoading((prev) => ({ ...prev, advanced: false }));
              })
            : Promise.resolve();

        await Promise.allSettled([...sectionPromises, advancedPromise]);
        if (generation !== requestGenRef.current) return null;

        const assembled = assembleDashboardResponse(sectionResultsRef.current, advancedData);
        return parseDashboardResponse(assembled, wantAdvanced);
    }, []);

    const dateFromKey = formatDashboardDate(dateRange.from);
    const dateToKey = formatDashboardDate(dateRange.to);

    const loadDashboard = useCallback(async (options?: {
        force?: boolean;
        cacheOnly?: boolean;
        allowNetworkIfNoCache?: boolean;
    }) => {
        const force = options?.force ?? false;
        const cacheOnly = options?.cacheOnly ?? false;
        const allowNetworkIfNoCache = options?.allowNetworkIfNoCache ?? false;
        const generation = ++requestGenRef.current;
        const from = formatDashboardDate(dateRangeRef.current.from);
        const to = formatDashboardDate(dateRangeRef.current.to);
        const mode = viewModeRef.current;
        const wantAdvanced = mode === 'advanced';
        const cacheKey = buildDashboardCacheKey(from, to, mode, session?.user_id);

        if (force) {
            setStaleDataAvailable(false);
            pendingPayloadRef.current = null;
        }

        let servedFromCache = false;

        if (!force) {
            const cached = await getDashboardCache(cacheKey);
            if (
                cached?.payload
                && !isLegacyDashboardCache(cached.payload)
                && generation === requestGenRef.current
            ) {
                if (mountedRef.current) {
                    applyPayload(cached.payload, wantAdvanced);
                    setCachedAt(new Date(cached.fetchedAt));
                    setLoading(false);
                    setRevalidating(false);
                }
                servedFromCache = true;
            }
        }

        // Date/view changes: cache hit only — never hit the network.
        if (cacheOnly || (servedFromCache && !force)) {
            if (!servedFromCache && cacheOnly && mountedRef.current) {
                applyPayload(
                    { base: { ...emptyDashboardState } as DashboardCachedPayload['base'] },
                    wantAdvanced,
                );
                setCachedAt(null);
                setLoading(false);
            }
            return;
        }

        const shouldFetchFromNetwork = force || allowNetworkIfNoCache;
        if (!shouldFetchFromNetwork) {
            if (mountedRef.current) {
                setLoading(false);
            }
            return;
        }

        if (mode === 'timeline' || mode === 'users') {
            if (mountedRef.current) {
                setLoading(false);
                setSectionLoading(emptySectionLoading(false, false));
            }
            return;
        }

        if (mountedRef.current) {
            setLoading(true);
            setSectionLoading(emptySectionLoading(true, wantAdvanced));
            setKpi(defaultKPI);
            setAdvanced(emptyAdvancedData);
            setSalesTrend([]);
            setSalesProfitBreakdown([]);
            setProjectedProfitBreakdown([]);
            setPaymentModes([]);
            setTopDebtors([]);
            setTopAgents([]);
            setInventoryByDepot([]);
            setRecentTransactions([]);
            setMovementCounts([]);
            setConversionCounts([]);
            setSalesByFamily([]);
            setSalesByCategory([]);
            setSalesByDepot([]);
            setSalesBySector([]);
            setSalesByAgent([]);
            setTopItems([]);
            setPaymentTypes([]);
            setPaymentsByUser([]);
            setPaymentsByDepot([]);
            setSupplierData([]);
            setSupplierPaymentModes([]);
            setTopSupplierBalances([]);
            setOverdueSupplies([]);
            setCreditRiskClients([]);
            setTopWallets([]);
            setWalletByTag([]);
        }

        let receivedFirstSection = false;

        try {
            const payload = await fetchSectionsFromNetwork(
                from,
                to,
                wantAdvanced,
                generation,
                (section, data) => {
                    if (generation !== requestGenRef.current || !mountedRef.current) return;
                    applySection(section, data);
                    if (!receivedFirstSection) {
                        receivedFirstSection = true;
                        setLoading(false);
                    }
                },
            );
            if (!payload || generation !== requestGenRef.current) {
                return;
            }

            const { saved, fetchedAt } = await persistPayload(cacheKey, from, to, mode, payload);
            if (mountedRef.current) {
                if (saved) {
                    setCachedAt(new Date(fetchedAt));
                }
                setStaleDataAvailable(false);
                pendingPayloadRef.current = null;
            }
        } catch (error) {
            console.error('Dashboard fetch failed:', error);
            if (mountedRef.current) {
                applyPayload(
                    { base: { ...emptyDashboardState } as DashboardCachedPayload['base'] },
                    wantAdvanced,
                );
            }
        } finally {
            if (generation === requestGenRef.current && mountedRef.current) {
                setLoading(false);
                setRevalidating(false);
            }
        }
    }, [
        applyPayload,
        applySection,
        fetchSectionsFromNetwork,
        persistPayload,
    ]);

    const applyPendingRefresh = useCallback(async () => {
        const pending = pendingPayloadRef.current;
        if (!pending) return;

        const from = formatDashboardDate(dateRangeRef.current.from);
        const to = formatDashboardDate(dateRangeRef.current.to);
        const mode = viewModeRef.current;
        const cacheKey = buildDashboardCacheKey(from, to, mode, session?.user_id);
        const wantAdvanced = mode === 'advanced';

        applyPayload(pending, wantAdvanced);
        const { saved, fetchedAt } = await persistPayload(cacheKey, from, to, mode, pending);
        if (saved) {
            setCachedAt(new Date(fetchedAt));
        }
        pendingPayloadRef.current = null;
        setStaleDataAvailable(false);
    }, [applyPayload, persistPayload]);

    const dismissStaleAlert = useCallback(() => {
        setStaleDataAvailable(false);
    }, []);

    const refresh = useCallback(() => {
        setRefreshGeneration((n) => n + 1);
        void loadDashboard({ force: true });
    }, [loadDashboard]);

    useEffect(() => {
        if (!hasMountedRef.current) {
            hasMountedRef.current = true;
            void loadDashboard({ allowNetworkIfNoCache: true });
            return;
        }
        void loadDashboard({ cacheOnly: true });
    }, [dateFromKey, dateToKey, viewMode, loadDashboard]);

    return (
        <DashboardContext.Provider
            value={{
                dateRange,
                setDateRange,
                viewMode,
                setViewMode,
                refreshGeneration,
                kpi,
                advanced,
                salesTrend,
                salesProfitBreakdown,
                projectedProfitBreakdown,
                paymentModes,
                topDebtors,
                topAgents,
                inventoryByDepot,
                recentTransactions,
                movementCounts,
                conversionCounts,
                salesByFamily,
                salesByCategory,
                salesByDepot,
                salesBySector,
                salesByAgent,
                topItems,
                paymentTypes,
                paymentsByUser,
                paymentsByDepot,
                supplierData,
                supplierPaymentModes,
                topSupplierBalances,
                overdueSupplies,
                creditRiskClients,
                topWallets,
                walletByTag,
                loading,
                sectionLoading,
                revalidating,
                staleDataAvailable,
                cachedAt,
                refresh,
                applyPendingRefresh,
                dismissStaleAlert,
                currency: appSettings?.BIZ_CURRENCY?.value || "USD",
            }}
        >
            {children}
        </DashboardContext.Provider>
    );
};

export const useDashboard = () => {
    const ctx = useContext(DashboardContext);
    if (!ctx) throw new Error('useDashboard must be used within DashboardProvider');
    return ctx;
};